/**
 * Frontend observability helpers — Web Vitals + API error reporting.
 * Batches fire-and-forget POSTs to /api/client-metrics (no PII).
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';
const ENABLED = (import.meta.env.VITE_CLIENT_METRICS_ENABLED ?? 'true') !== 'false';

const vitalQueue = [];
const errorQueue = [];
let flushTimer = null;
const FLUSH_MS = 5000;
const MAX_BATCH = 20;

function normalizeClientRoute(url) {
  if (!url || typeof url !== 'string') return 'unknown';
  try {
    const path = url.startsWith('http') ? new URL(url).pathname : url.split('?')[0];
    return path
      .replace(
        /\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi,
        '/:id',
      )
      .replace(/\/\d{2,}/g, '/:id');
  } catch {
    return 'unknown';
  }
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush().catch(() => {});
  }, FLUSH_MS);
}

async function flush() {
  if (!ENABLED) {
    vitalQueue.length = 0;
    errorQueue.length = 0;
    return;
  }

  const vitals = vitalQueue.splice(0, MAX_BATCH);
  const apiErrors = errorQueue.splice(0, MAX_BATCH);
  if (vitals.length === 0 && apiErrors.length === 0) return;

  const payload = { vitals, apiErrors };
  try {
    await fetch(`${API_BASE_URL}/api/client-metrics`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch {
    // Best-effort — never break the app for telemetry
  }

  if (vitalQueue.length > 0 || errorQueue.length > 0) scheduleFlush();
}

/**
 * Record a Web Vital sample (LCP, INP, CLS, FCP, TTFB).
 * @param {{ name: string, value: number, rating?: string }} metric
 */
export function enqueueVital(metric) {
  if (!ENABLED || !metric?.name) return;
  vitalQueue.push({
    name: String(metric.name).toUpperCase(),
    value: Number(metric.value),
    rating: metric.rating || undefined,
  });
  if (vitalQueue.length >= MAX_BATCH) {
    flush().catch(() => {});
  } else {
    scheduleFlush();
  }
}

/**
 * Record an API failure from the axios interceptor (status + path only).
 * @param {{ status?: number, url?: string }} info
 */
export function enqueueApiError(info) {
  if (!ENABLED) return;
  errorQueue.push({
    status: info?.status ?? 0,
    route: normalizeClientRoute(info?.url),
  });
  if (errorQueue.length >= MAX_BATCH) {
    flush().catch(() => {});
  } else {
    scheduleFlush();
  }
}

/**
 * Start collecting Core Web Vitals via the web-vitals library.
 */
export async function reportWebVitals() {
  if (!ENABLED) return;
  try {
    const { onCLS, onINP, onLCP, onFCP, onTTFB } = await import('web-vitals');
    const report = (metric) => enqueueVital(metric);
    onCLS(report);
    onINP(report);
    onLCP(report);
    onFCP(report);
    onTTFB(report);
  } catch {
    // Ignore if web-vitals fails to load
  }

  // Flush remaining samples when the tab is hidden / unloading
  const onHide = () => {
    if (document.visibilityState === 'hidden') flush().catch(() => {});
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', () => flush().catch(() => {}));
}

export { normalizeClientRoute };
